'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { CALYPSO } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import PropTypes from 'prop-types';
import UIIcon from 'UIComponents/icon/UIIcon';
import * as AssetBusinessType from 'ecosystem-marketplace-core/constants/groupTags/AssetBusinessType';
import * as ThemeQueryField from '../../constants/ThemeQueryField';
import { getAssetBusinessTypeI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import RouterFilterButton from '../storefront/RouterFilterButton';
import ThemeQuery from '../../records/ThemeQuery';
import ThemeResultsSectionDataContainer from '../storefront/results/ThemeResultsSectionDataContainer';
import { toSearchQuery } from '../../utils/themeQuery';
const MAX_RECOMMENDATION_COUNT = 3;

const ThemeRecommendations = ({
  businessTypes,
  isPreview,
  offeringId,
  onTrackInteraction,
  providerName
}) => {
  const {
    textAlign
  } = useResponsiveProps({
    textAlign: {
      xs: 'left'
    }
  });
  const businessTypesQuery = useMemo(() => new ThemeQuery({
    [ThemeQueryField.BUSINESS_TYPES]: businessTypes,
    [ThemeQueryField.LIMIT]: MAX_RECOMMENDATION_COUNT + 1
  }), [businessTypes]);
  const businessTypesTitle = useMemo(() => {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.themes.details.recommendations.businessTypesTitle",
      options: {
        businessTypes: I18n.formatList(businessTypes.map(businessType => I18n.text(getAssetBusinessTypeI18nKey(businessType))))
      }
    });
  }, [businessTypes]);
  const providerQuery = useMemo(() => new ThemeQuery({
    [ThemeQueryField.PROVIDERS]: [providerName],
    [ThemeQueryField.LIMIT]: MAX_RECOMMENDATION_COUNT + 1
  }), [providerName]);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'recommendations'
    }));
  }, [onTrackInteraction]);
  const renderRecommendationRouter = useCallback((themeQueryField, queryValue) => /*#__PURE__*/_jsxs(RouterFilterButton, {
    disabled: isPreview,
    onClick: () => trackInteraction('click see all link'),
    queryField: themeQueryField,
    style: {
      textAlign
    },
    value: queryValue,
    use: "transparent",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.themes.details.recommendations.seeAll"
    }), /*#__PURE__*/_jsx(UIIcon, {
      name: "right",
      size: "xxs",
      color: CALYPSO
    })]
  }), [isPreview, textAlign, trackInteraction]);
  return /*#__PURE__*/_jsx(MainSection, {
    responsive: true,
    children: /*#__PURE__*/_jsxs(MainSectionInner, {
      children: [/*#__PURE__*/_jsx(ThemeResultsSectionDataContainer, {
        maxColumns: MAX_RECOMMENDATION_COUNT,
        offeringId: offeringId,
        responsive: true,
        searchQuery: toSearchQuery(providerQuery),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.details.recommendations.providerTitle",
          options: {
            providerName
          }
        }),
        titleControls: renderRecommendationRouter(ThemeQueryField.PROVIDERS, providerName),
        trackInteraction: trackInteraction
      }), /*#__PURE__*/_jsx(ThemeResultsSectionDataContainer, {
        maxColumns: MAX_RECOMMENDATION_COUNT,
        offeringId: offeringId,
        responsive: true,
        searchQuery: toSearchQuery(businessTypesQuery),
        title: businessTypesTitle,
        titleControls: renderRecommendationRouter(ThemeQueryField.BUSINESS_TYPES, businessTypes),
        trackInteraction: trackInteraction
      })]
    })
  });
};

ThemeRecommendations.propTypes = {
  businessTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AssetBusinessType))).isRequired,
  isPreview: PropTypes.bool.isRequired,
  offeringId: PropTypes.number.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  providerName: PropTypes.string.isRequired
};
export default ThemeRecommendations;